import { useCallback } from 'react';
import * as _ from 'lodash-es';
import { formatInTimeZone } from 'date-fns-tz';

import { useAxiosClient } from '../store/AxiosClient';
import { useLoop } from '../store/Loop';
import { useMappingProperty } from '../store/Mapping';
import { useSpread } from '../store/Spread';
import { useEntityMeta } from '../store/EntityMeta';
import { useShowLoading } from '../../contexts/ShowLoading';
import { useInfoDialog } from '../../contexts/InfoDialog';

import { useStateWithGetter } from '../../utils/useStateWithGetter';
import { useThrowError } from '../../utils/useThrowError';
import { buildSheetsPayload } from '../../utils/sheetPayload';
import { exportExcelBase64 } from '../../utils/workbookRequest';
import { translate } from '../../utils/translations';

import {
  DATE_TIME_FORMAT,
  HTTP_RESPONSE_ERROR_CODE,
  TIME_ZONES,
} from '../../constants';

import { Result, RESULT } from '../../types';

import {
  BffCustomReportsApi,
  MappingDefinitionJsonModel,
  useApi,
} from './generated';

export const useSaveDraft = () => {
  const axiosClient = useAxiosClient();
  const customReportApi = useApi(axiosClient, BffCustomReportsApi);
  const { mappingProperties } = useMappingProperty();
  const { loops } = useLoop();
  const { spread } = useSpread();
  const {
    templateId,
    templateRowVersion,
    mappingRowVersion,
    mappingCreatedAt,
    setTemplateRowVersion,
    setMappingRowVersion,
  } = useEntityMeta();
  const [isApiRequesting, setIsApiRequesting, getIsApiRequesting] =
    useStateWithGetter(false);
  const { throwError } = useThrowError();
  const { setShowLoading } = useShowLoading();
  const infoDialog = useInfoDialog();

  const request = useCallback(
    async (
      template: string,
      mappingDefinition: MappingDefinitionJsonModel
    ): Promise<{
      result: Result;
      data?: {
        templateRowVersion: string;
        mappingRowVersion: string;
      };
    }> => {
      if (getIsApiRequesting()) return { result: RESULT.SKIPPED };

      setIsApiRequesting(true);

      try {
        const {
          data: { success, data },
        } = await customReportApi.customReportsPostSaveDraft({
          templateId,
          template,
          templateRowVersion,
          mappingDefinition,
          mappingDefinitionRowVersion: mappingRowVersion,
        });

        if (success && data) {
          // 各種rowVersionの更新
          setTemplateRowVersion(data.templateRowVersion);
          setMappingRowVersion(data.mappingDefinitionRowVersion);
          return {
            result: RESULT.SUCCESS,
            data: {
              templateRowVersion: data.templateRowVersion,
              mappingRowVersion: data.mappingDefinitionRowVersion,
            },
          };
        } else {
          // 通信は成功したが一時保存に失敗したパターン
          throwError(translate('COMMON.ERROR'));
          return { result: RESULT.FAILED };
        }
      } catch (e) {
        // リクエスト自体が失敗したパターン（200以外）
        // TODO: エラーハンドリングは後ほど実装
        const errorCode = _.get(e, 'response.data.errorCode');
        if (errorCode === HTTP_RESPONSE_ERROR_CODE.MSG_ERROR_COMMON_003) {
          const ok = await infoDialog({
            message: translate('VALIDATE.MSG_ERROR_COMMON_003'),
          });
          if (ok) return { result: RESULT.FAILED };
        }

        throwError(e);
        return { result: RESULT.FAILED };
      } finally {
        setIsApiRequesting(false);
      }
    },
    [templateId, templateRowVersion, mappingRowVersion]
  );

  const saveDraft = useCallback(async (): Promise<{
    result: Result;
    data?: {
      templateRowVersion: string;
      mappingRowVersion: string;
    };
  }> => {
    setShowLoading(true);

    if (!spread) return { result: RESULT.FAILED };

    const sheets = buildSheetsPayload(spread);

    const today = formatInTimeZone(
      new Date(),
      TIME_ZONES.TOKYO,
      DATE_TIME_FORMAT.DATE_TIME_TZ_ISO_FORMAT
    );

    const excelMapping = {
      sheets: sheets,
      createdAt: mappingCreatedAt || today,
      updatedAt: today,
    };

    const template = await exportExcelBase64(spread);

    const result = await request(template, excelMapping);

    setShowLoading(false);

    return result;
  }, [mappingProperties, loops, spread, request]);

  return { saveDraft };
};
